import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { apiRemote, ISubmission } from "@/api";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { TableSortLabel } from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const keyMappings: Record<string, string> = {
  problem: "Problem",
  schoolType: "School Type",
  location: "Location",
  studentPopulation: "Student Population",
  teacherCount: "Teacher Count",
  techResources: "Technology Resources",
  keyChallenges: "Key Challenges",
  additionalContext: "Additional Context",
  aiPrompt: "AI Prompt",
  aiTool: "AI Tool",
  aiEngine: "AI Engine",
  aiFinalProposal: "Ai Proposal",
  finalProposal: "Final Proposal",
  critique: "Critique",
  strengths: "Strengths",
  weaknesses: "Weaknesses",
  feasibility: "Feasibility",
  ethics: "Ethics",
  implementationSteps: "Implementation Steps",
  resourcesNeeded: "Resources Needed",
  expectedOutcomes: "Expected Outcomes",
}

const SubmissionManagement = () => {
  const [participants, setParticipants] = useState<ISubmission[] | []>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState("view"); // view | add | edit | delete
  const [selectedSubmission, setSelectedSubmission] = useState([]);
  const [expanded, setExpanded] = React.useState({});
  const [orderBy, setOrderBy] = useState<string>(''); // tên cột
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('desc'); // hướng sắp xếp

  const handleOpenDialog = (mode: any, submission: any) => {
    setDialogMode(mode);
    setSelectedSubmission(submission);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSubmission([]);
  };

  const fetchParticipants = async (searchTerm?: string) => {
    setLoading(true);
    try {
      const res = await apiRemote.getUserWithAiScore(page + 1, rowsPerPage, searchTerm, orderBy, orderDirection); // +1 vì page server thường bắt đầu từ 1
      setParticipants(res.data || []);
      setTotalRows(res.total || 0);
    } catch (error) {
      console.error("Failed to fetch participants:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParticipants(searchTerm);
  }, [page, rowsPerPage, orderBy, orderDirection]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const toggleExpand = (index: any) => {
    setExpanded((prev: any) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    fetchParticipants(searchTerm);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSort = (column: string) => {
    if (orderBy === column) {
      setOrderDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(column);
      setOrderDirection('asc');
    }
  };

  const exportToExcel =  async () => {
    // const res = await apiRemote.getUserWithAiScore(undefined, undefined, searchTerm, orderBy, orderDirection);
    // const data1 = res.data || [];
      
    if (participants.length === 0) {
      alert("No data to export");
      return;
    }
    const wb = XLSX.utils.book_new(); // Tạo workbook mới
  
    const wsData = [
      ["Rank", "Full Name", "Email", "Submitted Date", "Country", "AI Score", "AI Score Details", "Judge", "Score"], // Tiêu đề cột
      ...participants.map((contestant: any, index: any) => {
        let aiScoreDetails = "N/A";
  
        // Kiểm tra nếu dữ liệu JSON hợp lệ thì xử lý
        try {
          const aiData = JSON.parse(
            // contestant.ai_score_data.replace(/\\"/g, '"')
            contestant.ai_score_data.replace(/\\+/g, "")
          );
          const scores = aiData.scores
            ? Object.entries(aiData.scores)
                .map(([key, value]) => `${key.replace(/_/g, " ")}: ${value}`)
                .join("\n")
            : "No scores available";
  
          const feedback = aiData.feedback
            ? Object.entries(aiData.feedback)
                .map(([key, value]) => `${key.replace(/_/g, " ")}: ${value}`)
                .join("\n")
            : "No feedback available";
  
          aiScoreDetails = `Scores:\n${scores}\n\nFeedback:\n${feedback}\n\nSummary:\n${
            aiData.summary || "N/A"
          }`;
        } catch (error) {
          console.error("Error parsing AI score data:", error);
        }
  
        return [
          index + 1, // Rank
          contestant.display_name, // Contestant,
          contestant.user_email,
          contestant.latest_submission_at,
          contestant.countryName,
          contestant.ai_score, // AI Score
          aiScoreDetails, // AI Score Details (phân tích từ JSON)
          contestant.judge.display_name, // Judge
          contestant.score ?? 0, // Tổng điểm của Judge
        ];
      }),
    ];
  
    const ws = XLSX.utils.aoa_to_sheet(wsData); // Chuyển dữ liệu thành sheet
    ws["!cols"] = [
      { wch: 10 }, // Rank
      { wch: 20 }, // Contestant
      { wch: 10 }, // AI Score
      { wch: 40 }, // AI Score Details (tăng chiều rộng cột)
      { wch: 20 }, // Judge
      { wch: 10 }, // Score
    ];
  
    XLSX.utils.book_append_sheet(wb, ws, "Results"); // Thêm sheet vào workbook
  
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  
    saveAs(data, "scoring_results.xlsx"); // Lưu file Excel
  };

  return (
    <Box p={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Submission List</Typography>
        <Box display="flex" gap={2}>
          {/* <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="pending">Pending Review</MenuItem>
            <MenuItem value="reviewed">Reviewed</MenuItem>
          </Select> */}

          <TextField
            size="small"
            variant="outlined"
            placeholder="Search participant (name, email, country)..."
            value={searchTerm}
            onChange={handleChange}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />,
            }}
          />
          <Button variant="contained" onClick={handleSearch}>
            Search
          </Button>
          <Button variant="outlined" onClick={exportToExcel}>
            Export to Excel
          </Button>
        </Box>
      </Box>

      <TableContainer component={Card}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'display_name' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'display_name'}
                  direction={orderBy === 'display_name' ? orderDirection : 'asc'}
                  onClick={() => handleSort('display_name')}
                >
                  Full Name
                </TableSortLabel>
              </TableCell>
              {/* <TableCell>Username</TableCell> */}
              <TableCell sortDirection={orderBy === 'user_email' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'user_email'}
                  direction={orderBy === 'user_email' ? orderDirection : 'asc'}
                  onClick={() => handleSort('user_email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'latest_submission_at' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'latest_submission_at'}
                  direction={orderBy === 'latest_submission_at' ? orderDirection : 'asc'}
                  onClick={() => handleSort('latest_submission_at')}
                >
                  Submitted Date
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'countryName' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'countryName'}
                  direction={orderBy === 'countryName' ? orderDirection : 'asc'}
                  onClick={() => handleSort('countryName')}
                >
                  Country
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'ai_score' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'ai_score'}
                  direction={orderBy === 'ai_score' ? orderDirection : 'asc'}
                  onClick={() => handleSort('ai_score')}
                >
                  Ai Score
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <b>AI Score Details</b>
              </TableCell>
              <TableCell sortDirection={orderBy === 'score' ? orderDirection : false}>
                <TableSortLabel
                  active={orderBy === 'score'}
                  direction={orderBy === 'score' ? orderDirection : 'asc'}
                  onClick={() => handleSort('score')}
                >
                  Score
                </TableSortLabel>
              </TableCell>
              <TableCell>Assigned Judge</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                    <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : participants.length > 0 ? (
              participants.map((participant, index) => (
                <>
                <TableRow key={index}>
                  <TableCell>
                    {participant.display_name}
                  </TableCell>
                  {/* <TableCell>{participant.user}</TableCell> */}
                  <TableCell>{participant.user_email}</TableCell>
                  <TableCell>{participant.latest_submission_at}</TableCell>
                  <TableCell>{participant.countryName}</TableCell>
                  <TableCell>{participant.ai_score}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => toggleExpand(index)}>
                      {expanded[index] ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>{participant.score}</TableCell>
                  <TableCell>{participant.judge.display_name}</TableCell>
                  <TableCell>
                      <Tooltip title="View Details">
                        <IconButton onClick={() => handleOpenDialog("view", participant.submissions)}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      {/* <Tooltip title="Download">
                        <IconButton>
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip> */}
                  </TableCell>
                  {/* <TableCell>{participant.status}</TableCell> */}
                  {/* <TableCell align="right">
                            </TableCell> */}
                </TableRow>
                {expanded[index] && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="subtitle1">
                        <b>AI Score Breakdown:</b>
                      </Typography>
                      <ul>
                        {Object.entries(
                          JSON.parse(
                            participant.ai_score_data.replace(
                              /\\+/g,
                              ""
                            )
                          ).scores
                        ).map(([key, value]) => (
                          <li key={key}>
                            <b>{key.replace(/_/g, " ")}:</b> {value}
                          </li>
                        ))}
                      </ul>
                      <Typography variant="subtitle1">
                        <b>Feedback:</b>
                      </Typography>
                      <ul>
                        {Object.entries(
                          JSON.parse(
                            participant.ai_score_data.replace(
                              /\\+/g,
                              ""
                            )
                          ).feedback
                        ).map(([key, value]) => (
                          <li key={key}>
                            <b>{key.replace(/_/g, " ")}:</b> {value}
                          </li>
                        ))}
                      </ul>
                      <Typography variant="body1">
                        <b>Summary:</b>{" "}
                        {
                          JSON.parse(
                            participant.ai_score_data.replace(
                              /\\+/g,
                              ""
                            )
                          ).summary
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                </>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No participants found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100, 500]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {dialogMode === "view" && "Submission Details"}
          {dialogMode === "add" && "Add New Submission"}
          {dialogMode === "edit" && "Edit Submission Information"}
          {dialogMode === "delete" && "Confirm Deletion"}
        </DialogTitle>

        <DialogContent>
          {selectedSubmission?.map((submission) => (
            <Box key={submission.round_part_id} sx={{ mb: 3, p: 2, border: "1px solid #ddd", borderRadius: "8px" }}>
              <Typography variant="h6">{submission.section}</Typography>
              <Box sx={{ whiteSpace: "pre-wrap", background: "#f5f5f5", padding: "10px", borderRadius: "5px" }}>
                {submission.content && Object.entries(submission.content).map(([key, value]) => (
                  <Typography key={key} variant="body2" paragraph>
                    <strong>{keyMappings[key] || key}:</strong>{" "}
                    {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
                  </Typography>
                ))}
              </Box>
            </Box>
          ))}
        </DialogContent>

        <DialogActions>
          {dialogMode === "view" && (
            <Button onClick={handleCloseDialog}>Close</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubmissionManagement;
